import React, {Fragment, useEffect, useRef} from "react";
import styled from "@emotion/styled";
import {useSelector, useDispatch} from "react-redux";
import {
	getLeaderboardUserByUserId,
	getUserStore,
	getProfileByUserId,
	isProfileRequested,
	getCurrentUserId,
} from "modules/selectors";
import {useNavigate} from "react-router";
import {IconButton} from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import {useDigitParams, useMediaQuery, useTrapFocus} from "modules/utils";
import PickList from "pages/homePage/userProfile/PickList";
import UserInfo from "pages/homePage/userProfile/UserInfo";
import {fetchLeaderProfile} from "modules/actions/leaderboards";
import UserInfoRow from "pages/homePage/userProfile/userInfo/UserInfoRow";
import {Exist} from "components";
import {useTranslation} from "react-i18next";
import {MiddleLoading} from "components/MiddleLoading";
import {toggleBodyClass} from "modules/utils/dom_helpers";

const Wrapper = styled.dialog`
	position: absolute;
	left: 0;
	top: 0;
	width: 100%;
	height: 100%;
	background: #000;
	z-index: 12;
	padding: 10px;
	display: flex;
	flex-flow: column;
	gap: 10px;
	@media (max-width: 1100px) {
		padding: 10px 0 0;
	}

	@media (max-width: 880px) {
		position: fixed;
	}
`;

const CloseIcon = styled.div`
	display: flex;
	justify-content: flex-start;
	align-items: center;

	svg {
		color: #fff;
		height: 34px;
		width: 34px;
	}

	span {
		color: #ffffff;
		font-weight: 700;
		font-size: 16px;
		line-height: 130%;
	}
`;

const Content = styled.div`
	display: flex;
	flex-flow: row;
	gap: 24px;
	padding: 0 20px;
	overflow: auto;

	@media (max-width: 1100px) {
		flex-flow: column;
	}
`;

const StickyPosition = styled.div`
	order: 3;
	position: sticky;
	bottom: 0;
	z-index: 10;
`;
const useUserProfile = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const user = useSelector(getUserStore);
	const {userId} = useDigitParams("userId");
	const currentUserId = useSelector(getCurrentUserId);
	const userID = userId ? userId : currentUserId;
	const rankUser = useSelector(getLeaderboardUserByUserId)(Number(userId));
	const profile = useSelector(getProfileByUserId)(Number(userId));
	const isRequested = useSelector(isProfileRequested);

	useEffect(() => {
		// if (rankUser && !profile) {
		dispatch(
			fetchLeaderProfile({
				// userId: rankUser.userId,
				userId: Number(userID),
			})
		);
		// }
	}, [dispatch, userID]);

	return {
		navigate,
		rankUser,
		profile,
		isRequested,
		userId: user.currentUser?.id || 0,
	};
};

const UserProfile: React.FC = () => {
	const {navigate, rankUser, userId, profile, isRequested} = useUserProfile();
	const i18n = useTranslation();
	const modalRef = useRef<HTMLDialogElement>(null);

	useTrapFocus(modalRef, true);

	useEffect(() => {
		toggleBodyClass("disable_scroll", true, "profile");
		return () => {
			toggleBodyClass("disable_scroll", false, "profile");
		};
	}, []);

	const isMobile = useMediaQuery(`(max-width: 1100px)`);

	return (
		<Wrapper ref={modalRef} open={true} aria-label={`${String(rankUser?.username)} profile`}>
			<CloseIcon>
				<IconButton
					onClick={() => {
						navigate("../");
					}}
				>
					<KeyboardArrowLeftIcon />{" "}
					<span>{i18n.t("user_profile.page.back", "Back")}</span>
				</IconButton>
			</CloseIcon>

			<Content>
				{profile && (
					<Fragment>
						<Exist when={isMobile}>
							<UserInfo
								user={rankUser}
								userId={userId}
								profile={profile}
								isMobile={isMobile}
							/>
						</Exist>
						<PickList picksHistory={profile.predictions} />
						<Exist when={!isMobile}>
							<UserInfo
								user={rankUser}
								userId={userId}
								profile={profile}
								isMobile={isMobile}
							/>
						</Exist>
					</Fragment>
				)}
			</Content>
			<Exist when={isMobile}>
				<StickyPosition>
					<UserInfoRow user={rankUser} userId={userId} isMobile={true} />
				</StickyPosition>
			</Exist>
			<MiddleLoading when={isRequested} />
		</Wrapper>
	);
};

export default UserProfile;
